/* eslint-disable no-underscore-dangle */

import _ from 'underscore';
import moment from 'moment';
import { TargetableElement } from './targetable-element';
import commonHelper, { isBlank, formatDate } from '../helpers/common';
import Base, { baseInit } from './base';
import { Targeting } from './targeting';
import { Creative } from './creative';
import { TrackingPixel } from './tracking-pixel';
import { Kpi } from './kpi';
import {
  PT_ADDED_VALUE,
  PT_COST_PLUS,
  PT_FIXED_MARGIN,
  PT_CPM,
  PT_CPA,
  LIT_CHANGE,
  PROD_SEM,
  KPI_AWARENESS,
  KPI_FLIP_PRIMARY,
  KPI_FLIP_SECONDARY,
} from '../constant';
import { Note } from './note';
import LineItemProductType from './line-item-product-type';
import LineItemTargeting from './line-item-targeting';

const MinLineItemBudget = 0;

class LineItem extends Base {
  static validations = {
    // Validation methods should return an instance of Error if invalid, null otherwise.
    // All methods must accept a val to validate as the first arg and, optionally, a reference
    // to the object as the second arg.

    productCategoryId(val) {
      if (_.isNull(val)) {
        // _.isEmpty(1) return true which is buggy
        return new Error('Product Category is required');
      }
      return null;
    },

    productTypes(val) {
      if (_.isEmpty(val)) {
        return new Error('Product Type is required');
      }
      return null;
    },
    name(val) {
      if (_.isEmpty(val)) {
        return new Error('Line Item name is required');
      }
      return null;
    },
    audienceName(val) {
      if (_.isEmpty(val)) {
        return new Error('Line Item audience name is required');
      }
      return null;
    },

    flightStartDate(val, lineItem) {
      if (!val) {
        return new Error('Start date missing');
      }
      if (lineItem.flightEndDate) {
        const start = moment.utc(val);
        const end = moment.utc(lineItem.flightEndDate);
        if (start.isAfter(end)) {
          return new Error('Start date cannot be after the end date');
        }
      }
      return null;
    },

    flightRange(lineItem, lineItemsWithSameAdreadyId) {
      if (!lineItem) {
        return null;
      }

      if (!lineItem.flightStartDate || !lineItem.flightEndDate) {
        return null;
      }

      const sl = moment.utc(lineItem.flightStartDate);
      const el = moment.utc(lineItem.flightEndDate);
      const isInValidFlightDate = lineItemsWithSameAdreadyId.some((li) => {
        const s = formatDate(li.flightStartDate);
        const e = formatDate(li.flightEndDate);
        if (isBlank(s) || isBlank(e)) {
          return false;
        }
        const result = (sl.isBefore(s) && el.isBefore(s)) || (sl.isAfter(e) && el.isAfter(e));
        return !result;
      });
      if (isInValidFlightDate) {
        return new Error('flight dates overlap with other line item flight date');
      }
      return null;
    },

    flightEndDate(val, lineItem) {
      if (!val) {
        return new Error('End date missing');
      }
      if (lineItem.flightStartDate) {
        const end = moment.utc(val);
        const start = moment.utc(lineItem.flightStartDate);
        if (end.isBefore(start)) {
          return new Error('End date cannot be before the start date');
        }
      }
      return null;
    },

    impressions(val, lineItem) {
      if (lineItem && lineItem.pricingType === PT_ADDED_VALUE && val <= 0) {
        return new Error('Must enter impressions for Added Value pricing');
      }
      return null;
    },

    targetings(targetings) {
      const byTargeting = targetings
        .map((targeting) => TargetableElement.validations.value(targeting.value, targeting))
        .filter((s) => s !== null);

      if (byTargeting.length === 0) {
        // only empty ones were found, raise err
        return null;
      }
      return new Error('Invalid Targeting Value');
    },

    pricingType(val, lineItem) {
      if (commonHelper.isNil(val) || !val) {
        return new Error('Pricing Type is required');
      }
      if (val === PT_CPM) {
        if (_.isNull(lineItem.price) && (_.isNull(lineItem.fixCpm) || lineItem.fixCpm === 0)) {
          return new Error('Price is required for CPM pricing');
        }
      } else if (val === PT_COST_PLUS) {
        if (_.isNull(lineItem.price)) {
          return new Error('Percentage is required for Cost+ pricing');
        }
      } else if (val === PT_FIXED_MARGIN) {
        if (_.isNull(lineItem.price)) {
          return new Error('Percentage is required for Fixed Margin pricing');
        }
      } else if (val === PT_CPA) {
        if (_.isNull(lineItem.price)) {
          return new Error('Price is required for CPA pricing');
        }
      }
      return null;
    },
  };

  _uuid = null; // String - internal field used for referencing unsaved line items
  _readonly = false; // Boolean - internal field for making lineitem read only

  id = null; // Integer
  planId = null; // Integer
  assignedTo = null; // Integer
  statusId = null; // Set Default to null
  workflowId = null; // Set Default to null
  taskTypeId = null; // Set Default to null
  qa = null; // Array
  billingType = null; // Set Default to null
  lineItemBudget = 0; // Integer
  budgetGroupId = null;
  productCategoryId = null; // Integer
  contactUserId = null;
  customUserId = null;
  customOrderId = null; // Integer
  productTypes = []; // [ LineItemProductType() ]
  targetings = []; // [LineItemTargeting]

  trackingPixels = null; // [ TrackingPixel() ]
  deviceTypes = [];
  vendors = [];

  creatives = null; // [ Creative{} ]
  siteRetargeting = null;
  retargetingNotes = null;
  note = null; // Note{}
  frequency = null; // Text
  dayParting = null; // Text
  flip = false; // Boolean
  incrementality = false; // Boolean
  managementFeeOnly = false; // Boolean
  audienceName = null; // Text

  // TODO: internal fields should be prefixed with _ (like _uuid)
  updatedLineItem = null;
  name = null; // String - line item name
  flightStartDate = null;
  flightEndDate = null;

  kpis = null;

  flagId = null; // Integer flag color id

  adreadyId = null;
  externalName = null; // String

  pricingType = null; // String (enum)
  price = null; // Float
  mediaSpend = null; // Float
  fixCpm = 0; // Float
  impressions = null; // Integer
  crLineItems = []; // change request lineItems, it should have only one at a time
  flag = false; // Boolean flag

  frequencyRecency = null;

  _isStatusChanged = false; // if there is status change it became true.
  _selected = false; // Boolean: checkbox selected for some action.
  _expanded = false;
  _isWorkflowChanged = false; // if there is workflow change it became true.
  spend = 0;
  pace = 0;
  tagsTrackers = false;
  constructor(data) {
    super(data);
    baseInit(this, data, true);

    if (this.cpm === null) {
      this.cpm = 0;
    }
    if (this.targeting === null) {
      this.targeting = new Targeting();
    }
    if (isBlank(this.targetings)) {
      this.targetings = [];
    } else {
      this.targetings = this.targetings.map((t) => new LineItemTargeting(t));
    }
    if (isBlank(this.productTypes)) {
      this.productTypes = [];
    } else {
      this.productTypes = this.productTypes.map((p) => new LineItemProductType(p));
    }
    if (isBlank(this.creatives)) {
      this.creatives = [];
    } else {
      this.creatives = this.creatives.map((c) => new Creative(c));
    }
    if (isBlank(this.trackingPixels)) {
      this.trackingPixels = [];
    } else {
      this.trackingPixels = this.trackingPixels.map((p) => new TrackingPixel(p));
    }
    if (this.flightStartDate) {
      this.flightStartDate = moment.utc(this.flightStartDate).toDate();
    }
    if (this.flightEndDate) {
      this.flightEndDate = moment.utc(this.flightEndDate).toDate();
    }
    if (isBlank(this.kpis)) {
      this.kpis = [];
    } else {
      this.kpis = this.kpis.map((kpi) => new Kpi(kpi));
    }
    if (this.note !== null) {
      this.note = new Note(this.note);
    }
    if (!isBlank(this.crLineItems)) {
      this.crLineItems = this.crLineItems.map((li) => new LineItem(li));
    }
    if (this.spend === null) {
      this.spend = 0;
    }
    if (this.pace === null) {
      this.pace = 0;
    }
    if (this.tagsTrackers === null) {
      this.tagsTrackers = false;
    }
  }

  newUUID(resetIDs) {
    super.newUUID(resetIDs);
    if (resetIDs) {
      // recurse to nested objects
      if (!isBlank(this.creatives)) {
        this.creatives.forEach((creative) => {
          creative.newUUID(resetIDs);
        });
      }
      if (this.note && this.note.newUUID) {
        this.note.newUUID(resetIDs);
      }
      if (!isBlank(this.kpis)) {
        this.kpis.forEach((kpi) => {
          kpi.newUUID(resetIDs);
        });
      }
      if (!isBlank(this.deviceTypes)) {
        this.deviceTypes.forEach((dt) => {
          // simple type
          dt.id = null;
        });
      }
      if (!isBlank(this.vendors)) {
        this.vendors.forEach((v) => {
          // simple type
          v.id = null;
        });
      }
      if (!isBlank(this.trackingPixels)) {
        this.trackingPixels.forEach((tp) => {
          // non-standard obj layout
          tp.idKey = null;
        });
      }
      if (!isBlank(this.targetings)) {
        this.targetings.forEach((targeting) => {
          targeting.newUUID(resetIDs);
        });
      }
    }
  }

  isChangeRequest() {
    return this.type === LIT_CHANGE;
  }

  /**
   * Returns TRUE if this line item has the given product type ID
   *
   * @param {Number} needleId productTypeId to test for
   * @returns {Boolean}
   */
  hasProductTypeId(needleId) {
    return (
      !isBlank(this.productTypes) && this.productTypes.some((pt) => needleId === pt.productTypeId)
    );
  }

  /**
   * Search list of targetings for ones which are of the correct
   * targetingCategory type.
   *
   * @param {String} category
   * @param {Object} cache To use for looking up Targetable Elements
   */
  getTargetingsByCategory(category, cache) {
    return LineItem.getTargetingsByCategory(category, this.targetings, cache);
  }

  isDirty() {
    if (this.updatedLineItem === null) {
      return false;
    }
    return this.updatedLineItem;
  }

  isValidCreatives(val, lineItem, isMni) {
    if (isMni) {
      const errReq = new Error('At least one creative is required');
      if (_.isEmpty(val)) {
        return errReq;
      }
    }
    if (lineItem && this.hasProductTypeId(PROD_SEM)) {
      return null;
    }
    const creatives = val.map((c) => (c instanceof Creative ? c : new Creative(c)));
    const creativesVal = isMni ? creatives : creatives.filter((c) => !c.isEmpty());
    // filter creatives by their status - remove empty ones from the list
    const byStatus = creativesVal.map((c) => (c.isEmpty() ? false : c.isValid()));
    const valid = byStatus.every((s) => s === true);
    if (!valid) {
      // at least one invalid (non-empty) creative
      return new Error('Invalid creative');
    }
    return null;
  }

  static isValidLineItemBudget(val, lineItem, isMni) {
    if (lineItem.pricingType === PT_ADDED_VALUE) {
      return null; // budget is not required (nor allowed) for this type
    }
    if (commonHelper.isNil(val)) {
      return new Error('Budget is required');
    }
    if (isBlank(lineItem.budgetGroupId) && val <= MinLineItemBudget) {
      return new Error(`Budget must be greater than $${MinLineItemBudget}`);
    }
    // MERD Account - Min $500 per/30 days
    if (isMni) {
      const sl = moment.utc(lineItem.flightStartDate);
      const el = moment.utc(lineItem.flightEndDate);
      const diffInDays = el.diff(sl, 'days');
      if (diffInDays) {
        const month = Math.ceil(diffInDays / 30);
        const minBudget = month * 500;
        if (val < minBudget) {
          return new Error(`Budget must be at least $${minBudget}`);
        }
      }
    }
    return null;
  }

  static getTargetingsByCategory(category, targetings, cache) {
    if (isBlank(targetings)) {
      return [];
    }
    return targetings.filter((t) => {
      const te = cache[t.targetableElementId];
      return te && te.targetingType === category;
    });
  }

  static isValidKpis(val) {
    const kpis = val.map((k) => (k instanceof Kpi ? k : new Kpi(k)));
    const invalidKpis = kpis.filter(
      (k) =>
        isBlank(k.benchmark) &&
        [KPI_FLIP_PRIMARY, KPI_FLIP_SECONDARY].includes(k.kpiId) &&
        Number.parseInt(k.value, 10) !== KPI_AWARENESS
    );
    if (invalidKpis.length > 0) {
      return new Error('BenchMark is required.');
    }
    return null;
  }

  isValidLineItem(isMni) {
    let inValid = isBlank(this.id) || !this.isValid();
    if (inValid) {
      return !inValid;
    }
    if (!isMni) {
      inValid = _.isNull(this.billingType);
    }
    if (!inValid) {
      inValid = !isBlank(this.isValidCreatives(this.creatives, this, isMni));
    }
    if (!inValid) {
      inValid = !isBlank(LineItem.isValidLineItemBudget(this.lineItemBudget, this, isMni));
    }
    if (!inValid) {
      inValid = !isBlank(LineItem.isValidKpis(this.kpis));
    }
    return !inValid;
  }
}

export { LineItem };
